import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  IconButton,
  TextField,
  useToast,
} from "@stagvista/ui";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, ChevronUp, ListMusic, Pencil, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { useCloudSession } from "../hooks/use-cloud-session.js";
import { CloudApiError } from "../lib/cloud-api-client.js";

export const Route = createFileRoute("/events/$eventId")({
  component: EventServicesPage,
});

/** `apps/cloud-api`'s `Event` response shape — only the fields this page reads. */
interface EventDto {
  readonly id: string;
  readonly name: string;
}

/** `apps/cloud-api`'s `Service` response shape — only the fields this page reads. */
interface ServiceDto {
  readonly id: string;
  readonly name: string;
  readonly position: string;
}

function EventServicesPage(): React.ReactElement {
  const { session } = useCloudSession();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!session) void navigate({ to: "/sign-in" });
  }, [session, navigate]);

  if (!session) return <></>;
  return <EventServicesView />;
}

function EventServicesView(): React.ReactElement {
  const { eventId } = useParams({ from: "/events/$eventId" });
  const { client } = useCloudSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [event, setEvent] = React.useState<EventDto | null>(null);
  const [services, setServices] = React.useState<ServiceDto[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      const [loadedEvent, loadedServices] = await Promise.all([
        client.request<EventDto>("GET", `/v1/events/${eventId}`),
        client.request<ServiceDto[]>("GET", `/v1/events/${eventId}/services`),
      ]);
      setEvent(loadedEvent);
      setServices(loadedServices.slice().sort((a, b) => (a.position < b.position ? -1 : 1)));
    } catch (err) {
      setLoadError(
        err instanceof CloudApiError
          ? (err.problem.detail ?? err.problem.title)
          : "Couldn't load this event.",
      );
    }
  }, [client, eventId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  function reportError(err: unknown, fallback: string): void {
    const message =
      err instanceof CloudApiError ? (err.problem.detail ?? err.problem.title) : fallback;
    toast({ tone: "error", title: fallback, description: message });
  }

  async function handleAdd(name: string): Promise<void> {
    try {
      await client.request("POST", `/v1/events/${eventId}/services`, { name });
      await load();
    } catch (err) {
      reportError(err, "Couldn't add service");
    }
  }

  async function handleRename(serviceId: string, name: string): Promise<void> {
    try {
      await client.request("PATCH", `/v1/services/${serviceId}`, { name });
      setEditingId(null);
      await load();
    } catch (err) {
      reportError(err, "Couldn't rename service");
    }
  }

  async function handleDelete(service: ServiceDto): Promise<void> {
    try {
      await client.request("DELETE", `/v1/services/${service.id}`);
      await load();
    } catch (err) {
      reportError(err, "Couldn't delete service");
    }
  }

  async function handleMove(serviceId: string, direction: "up" | "down"): Promise<void> {
    if (!services) return;
    const index = services.findIndex((s) => s.id === serviceId);
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (index === -1 || targetIndex < 0 || targetIndex >= services.length) return;

    // Moving up means "sit right before the sibling two spots up" (or at
    // the very start); moving down is the mirror — one before/after id,
    // matching the same convention `CreateServiceUseCase` uses.
    const body =
      direction === "up"
        ? { beforeId: services[targetIndex - 1]?.id, afterId: services[targetIndex]!.id }
        : { beforeId: services[targetIndex]!.id, afterId: services[targetIndex + 1]?.id };

    try {
      await client.request("POST", `/v1/services/${serviceId}/move`, body);
      await load();
    } catch (err) {
      reportError(err, "Couldn't reorder service");
    }
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h1 text-fg-primary">{event?.name ?? "Event"}</h1>
        <Button variant="ghost" size="sm" onClick={() => void navigate({ to: "/" })}>
          <ArrowLeft className="size-4" />
          Back to events
        </Button>
      </div>

      {loadError ? <p className="text-caption text-status-critical">{loadError}</p> : null}

      <AddServiceForm onAdd={handleAdd} />

      {services === null ? (
        <p className="text-caption text-fg-tertiary">Loading…</p>
      ) : services.length === 0 ? (
        <p className="text-caption text-fg-tertiary">No services yet — add one above.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {services.map((service, index) =>
            editingId === service.id ? (
              <RenameServiceForm
                key={service.id}
                service={service}
                onCancel={() => setEditingId(null)}
                onSubmit={(name) => handleRename(service.id, name)}
              />
            ) : (
              <ServiceRow
                key={service.id}
                service={service}
                isFirst={index === 0}
                isLast={index === services.length - 1}
                canDelete={services.length > 1}
                onRename={() => setEditingId(service.id)}
                onOpen={() =>
                  navigate({ to: "/services/$serviceId", params: { serviceId: service.id } })
                }
                onDelete={() => handleDelete(service)}
                onMoveUp={() => handleMove(service.id, "up")}
                onMoveDown={() => handleMove(service.id, "down")}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function AddServiceForm({ onAdd }: { onAdd: (name: string) => Promise<void> }): React.ReactElement {
  const [name, setName] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onAdd(name.trim());
      setName("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add a service</CardTitle>
      </CardHeader>
      <CardBody>
        <form className="flex items-end gap-2" onSubmit={(e) => void handleSubmit(e)}>
          <div className="flex-1">
            <TextField
              label="Service name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 1st Service"
            />
          </div>
          <Button type="submit" loading={submitting}>
            <Plus className="size-4" />
            Add
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}

function RenameServiceForm({
  service,
  onCancel,
  onSubmit,
}: {
  service: ServiceDto;
  onCancel: () => void;
  onSubmit: (name: string) => Promise<void>;
}): React.ReactElement {
  const [name, setName] = React.useState(service.name);
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(name.trim());
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="flex items-end gap-2 rounded-md border border-line-subtle p-3"
      onSubmit={(e) => void handleSubmit(e)}
    >
      <TextField
        label="Service name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        containerClassName="flex-1"
      />
      <Button type="button" variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" loading={submitting}>
        Save
      </Button>
    </form>
  );
}

function ServiceRow({
  service,
  isFirst,
  isLast,
  canDelete,
  onRename,
  onOpen,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  service: ServiceDto;
  isFirst: boolean;
  isLast: boolean;
  canDelete: boolean;
  onRename: () => void;
  onOpen: () => void;
  onDelete: () => Promise<void>;
  onMoveUp: () => Promise<void>;
  onMoveDown: () => Promise<void>;
}): React.ReactElement {
  const [busy, setBusy] = React.useState(false);

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle>
          <button
            type="button"
            className="flex items-center gap-2 hover:underline"
            onClick={onOpen}
          >
            <ListMusic className="size-4 text-fg-tertiary" />
            {service.name}
          </button>
        </CardTitle>
        <div className="flex items-center gap-1">
          <IconButton
            aria-label="Move up"
            disabled={isFirst || busy}
            onClick={() => void run(onMoveUp)}
          >
            <ChevronUp />
          </IconButton>
          <IconButton
            aria-label="Move down"
            disabled={isLast || busy}
            onClick={() => void run(onMoveDown)}
          >
            <ChevronDown />
          </IconButton>
        </div>
      </CardHeader>
      <CardBody className="flex items-center justify-end gap-1.5">
        <IconButton aria-label={`Rename ${service.name}`} disabled={busy} onClick={onRename}>
          <Pencil />
        </IconButton>
        <IconButton
          aria-label={`Delete ${service.name}`}
          disabled={!canDelete || busy}
          title={canDelete ? undefined : "An event must have at least one service"}
          onClick={() => void run(onDelete)}
        >
          <Trash2 />
        </IconButton>
      </CardBody>
    </Card>
  );
}
